import { DateTime } from 'luxon';
import { TFunction } from 'i18next';
import { Quote, QuoteProduct } from '../../models';
import {
  getSanitizedExportQuote,
  getSanitizedExportQuoteProducts,
} from '../../utils/quote';
import { CurrencyFormatter } from '../../utils';
import { CableLengthFormatter } from '../../utils/units';
import { Language, MeasurementUnits } from '../../enums';
import report from './report';

const PREFIX = 'Quote';
const NAMESPACE = 'quotes.fields';
const EXPORTED_KEYS = [
  'agencyName',
  'companyName',
  'customerName',
  'typeOfBuy',
  'projectName',
  'contractorName',
  'nciProjectId',
  'shippingYearMonth',
  'shippingLocation',
  'expirationTime',
  'totalQuoteValue',
  'totalCableLength',
];

const EXPORTED_PRODUCT_KEYS = [
  'lineItem',
  'partNumber',
  'description',
  'quantity',
  'minimumOrderQuantity',
  'price',
  'discount',
  'netUnitPrice',
  'extendedPrice',
  'customLeadTime',
  'purchaseStatus',
];

class quoteCsv extends report<Quote> {
  private readonly data: Quote;
  private readonly currencyFormatter: CurrencyFormatter;
  private readonly cableLengthFormatter: CableLengthFormatter;
  private readonly units: MeasurementUnits;
  private readonly priceUnits: MeasurementUnits;
  private readonly language: Language;

  constructor(
    data: Quote,
    t: TFunction,
    currencyFormatter: CurrencyFormatter,
    cableLengthFormatter: CableLengthFormatter,
    units: MeasurementUnits,
    priceUnits: MeasurementUnits,
    language: Language
  ) {
    super(PREFIX, NAMESPACE, EXPORTED_KEYS, t);

    this.data = data;
    this.currencyFormatter = currencyFormatter;
    this.cableLengthFormatter = cableLengthFormatter;
    this.units = units;
    this.priceUnits = priceUnits;
    this.language = language;
  }

  protected override getFilename(): string {
    const now = DateTime.now().toFormat('LLddyyyyHHmmss');
    const name = this.data.quoteNumber
      ? `${PREFIX}_${this.data.quoteNumber}`
      : `Draft_for_${this.data.customerName}`;

    return `${name}_${now}.csv`;
  }

  protected getSanitizedData(): Quote[] {
    const sanitizedQuote = getSanitizedExportQuote(
      this.data,
      this.t,
      this.currencyFormatter
    ) as unknown as Quote;
    return [
      getSanitizedExportQuoteProducts(
        sanitizedQuote,
        this.t,
        this.currencyFormatter,
        this.cableLengthFormatter,
        this.units,
        this.priceUnits,
        this.language
      ) as unknown as Quote,
    ];
  }

  protected getProductHeaders(): string[] {
    return EXPORTED_PRODUCT_KEYS.map(
      (key) => `"${this.t(`${this.namespace}.${key}`)}"`
    );
  }

  protected override getCSVData(data: Quote[]): string {
    const csvContent = super.getCSVData(data);
    const csvRows = [];

    if (!data[0] || data[0].products.length === 0) {
      return csvContent;
    }

    csvRows.push(this.getProductHeaders().join(','));

    for (const product of data[0].products) {
      const values = EXPORTED_PRODUCT_KEYS.map((value) => {
        let result = product[value as keyof QuoteProduct] as unknown;

        if (result === undefined) {
          result = '';
        }

        if (typeof result === 'string') {
          result = result.replace(/"/g, '""');
        }

        return `"${result}"`;
      }).join(',');
      csvRows.push(values);
    }

    return csvContent + '\r\n' + csvRows.join('\r\n');
  }

  static generate(
    data: Quote,
    t: TFunction,
    currencyFormatter: CurrencyFormatter,
    cableLengthFormatter: CableLengthFormatter,
    units: MeasurementUnits,
    priceUnits: MeasurementUnits,
    language: Language
  ) {
    const report = new quoteCsv(
      data,
      t,
      currencyFormatter,
      cableLengthFormatter,
      units,
      priceUnits,
      language
    );

    report.generateCSV();
  }
}

export default quoteCsv;
